import * as Sentry from "@sentry/react";
import type { FormatZodErrorIssue } from "@Simplicity-Education/core";

/** Cap on `PortalApiError.rawBody` so a full HTML error page can't bloat a Sentry event. */
const RAW_BODY_MAX_CHARS = 2000;
/** Caps on the body excerpt inside `detail`, and on the details line a super admin sees. */
const DETAIL_BODY_MAX_CHARS = 200;
const DETAILS_LINE_MAX_CHARS = 300;

/** What a client sees for anything that is not a written refusal; the specifics go to Sentry. */
const FALLBACK_MESSAGE = "Something went wrong. We've been notified.";

/** Dottie routes: an error body there may echo chat content, which must never reach Sentry. */
const DOTTIE_PATH = /\/api\/agent\//;

let errorDetailsVisible = false;

/**
 * Super admins get a details line under the client message. OFF by default, so an
 * error thrown before the viewer's role is known reads exactly as a client sees it.
 */
export function setErrorDetailsVisible(visible: boolean): void {
  errorDetailsVisible = visible;
}

/**
 * Error thrown by `portalFetch` for any non-ok, non-401 response. Preserves the
 * HTTP status and, when the server returned a JSON body with oRPC-style
 * `data.issues`, exposes those so consumers can run them through
 * `formatZodError()` (SIM-1005) for a friendly message.
 */
export class PortalApiError extends Error {
  status: number;
  data?: { issues?: FormatZodErrorIssue[] };
  /**
   * Truncated body of a response with no usable JSON `message`, for diagnostics.
   * Never render it: it can be an HTML error page or proxy text.
   */
  rawBody?: string;
  /** Technical one-liner (code, status, server text, request) for staff and Sentry, never clients. */
  detail?: string;

  constructor(
    message: string,
    status: number,
    data?: { issues?: FormatZodErrorIssue[] },
    rawBody?: string,
    detail?: string,
  ) {
    super(message);
    this.name = "PortalApiError";
    this.status = status;
    if (data) this.data = data;
    if (rawBody) this.rawBody = rawBody;
    if (detail) this.detail = detail;
  }
}

interface ParsedError {
  /** What the client is shown. */
  message: string;
  /** Neither a written refusal nor a validation error: the client gets the fallback, Sentry hears. */
  unexpected: boolean;
  code?: string;
  serverMessage?: string;
  dataDetail?: string;
  data?: { issues?: FormatZodErrorIssue[] };
  rawBody?: string;
}

interface RequestInfo {
  method: string;
  path: string;
}

interface BodyText {
  serverMessage?: string;
  dataDetail?: string;
  rawBody?: string;
}

function tryParseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

function extractIssues(json: Record<string, unknown>): FormatZodErrorIssue[] | undefined {
  const issues = asRecord(json.data).issues;
  return Array.isArray(issues) ? (issues as FormatZodErrorIssue[]) : undefined;
}

/**
 * Classify a non-ok body (oRPC shape `{ code, message, data: { issues, detail } }`). Only a 4xx
 * with a written `message` reaches the client verbatim — client code parses some of those, e.g.
 * `removedColumnError.ts`. `data.issues` are always kept for `formatZodError`.
 */
function parseErrorBody(status: number, text: string): ParsedError {
  const record = asRecord(text ? tryParseJson(text) : undefined);
  const serverMessage = nonEmptyString(record.message);
  const issues = extractIssues(record);
  const isClientError = status >= 400 && status < 500;
  const parsed: ParsedError = {
    message: isClientError && serverMessage ? serverMessage : FALLBACK_MESSAGE,
    unexpected: !isClientError || (!serverMessage && !issues),
    code: nonEmptyString(record.code),
    serverMessage,
    dataDetail: nonEmptyString(asRecord(record.data).detail),
  };
  if (issues) parsed.data = { issues };
  if (text && !serverMessage) parsed.rawBody = text.slice(0, RAW_BODY_MAX_CHARS);
  return parsed;
}

function describeRequest(url: string, init?: RequestInit): RequestInfo {
  const query = url.indexOf("?");
  return {
    method: (init?.method ?? "GET").toUpperCase(),
    path: query === -1 ? url : url.slice(0, query),
  };
}

/** Server-supplied text worth a diagnosis, minus what the client was already shown. */
function bodyText(parsed: ParsedError, req: RequestInfo): BodyText {
  if (DOTTIE_PATH.test(req.path)) return {};
  return {
    serverMessage: parsed.serverMessage === parsed.message ? undefined : parsed.serverMessage,
    dataDetail: parsed.dataDetail,
    rawBody: parsed.rawBody,
  };
}

function oneLine(text: string, max: number): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
}

function composeDetail(status: number, parsed: ParsedError, req: RequestInfo): string {
  const body = bodyText(parsed, req);
  return [
    parsed.code ? `${parsed.code} (${status})` : String(status),
    body.serverMessage,
    body.dataDetail,
    `${req.method} ${req.path}`,
    body.rawBody ? oneLine(body.rawBody, DETAIL_BODY_MAX_CHARS) : undefined,
  ]
    .filter((part) => part !== undefined)
    .join(" · ");
}

/** A sonner description collapses the newline to a space, which still reads as a second clause. */
function withDetails(message: string, detail: string): string {
  if (!errorDetailsVisible) return message;
  return `${message}\nDetails: ${oneLine(detail, DETAILS_LINE_MAX_CHARS)}`;
}

const ID_SEGMENT = /^(\d+|[0-9a-f-]{32,36}|c[a-z0-9]{20,})$/i;

/** Every fallback shares one message and one throw site, so group by route or they collapse into one issue. */
function routeTemplate(path: string): string {
  return path
    .split("/")
    .map((segment) => (ID_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");
}

function reportUnexpected(err: PortalApiError, parsed: ParsedError, req: RequestInfo): void {
  const extra: Record<string, unknown> = {
    status: err.status,
    method: req.method,
    path: req.path,
    code: parsed.code,
    ...bodyText(parsed, req),
  };
  const route = routeTemplate(req.path);
  Sentry.captureException(err, {
    level: "error",
    fingerprint: ["portal-api", String(err.status), req.method, route],
    tags: { portal_api_status: String(err.status), portal_api_route: route },
    extra: Object.fromEntries(Object.entries(extra).filter(([, value]) => value !== undefined)),
  });
}

async function buildError(res: Response, url: string, init?: RequestInit): Promise<PortalApiError> {
  const text = await res.text().catch(() => "");
  const parsed = parseErrorBody(res.status, text);
  const req = describeRequest(url, init);
  const detail = composeDetail(res.status, parsed, req);
  const message = withDetails(parsed.message, detail);
  const err = new PortalApiError(message, res.status, parsed.data, parsed.rawBody, detail);
  if (parsed.unexpected) reportUnexpected(err, parsed, req);
  return err;
}

/**
 * Typed fetch wrapper for portal oRPC routes (OpenAPI-style REST).
 * Redirects to /portal/login on 401. Throws `PortalApiError` on other non-ok
 * responses; the error preserves the Zod issues from oRPC's validation
 * response so `formatZodError(err)` can produce a friendly description.
 */
export async function portalFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { ...init, credentials: "include" });
  if (res.status === 401) {
    window.location.href = "/portal/login";
    return Promise.reject(new Error("Unauthorized"));
  }
  if (!res.ok) throw await buildError(res, url, init);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}
